import * as React from 'react';
import { useField, Field, HelpText, Label } from '@finn-no/troika-react-input';
import classNames from 'classnames';
import warning from 'tiny-warning';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var Checkbox = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
      disabled = _ref.disabled,
      error = _ref.error,
      helpText = _ref.helpText,
      id = _ref.id,
      label = _ref.label,
      style = _ref.style,
      props = _objectWithoutPropertiesLoose(_ref, ["className", "disabled", "error", "helpText", "id", "label", "style"]);

  var _useField = useField({
    id: id,
    hasError: error,
    hasHelpText: Boolean(helpText)
  }),
      controlProps = _useField.controlProps,
      helpTextProps = _useField.helpTextProps;

  return /*#__PURE__*/React.createElement("div", {
    className: classNames('input-toggle', {
      'input-toggle--is-disabled': disabled
    }, className),
    style: style
  }, /*#__PURE__*/React.createElement("input", _extends({
    disabled: disabled,
    ref: ref,
    type: "checkbox"
  }, controlProps, props)), /*#__PURE__*/React.createElement("label", {
    htmlFor: controlProps.id
  }, label), helpText && /*#__PURE__*/React.createElement(Field, {
    isInvalid: error
  }, /*#__PURE__*/React.createElement(HelpText, helpTextProps, helpText)));
});
Checkbox.displayName = "Checkbox";

var CheckboxGroup = function CheckboxGroup(_ref) {
  var children = _ref.children,
      error = _ref.error,
      helpText = _ref.helpText,
      id = _ref.id,
      label = _ref.label,
      onChange = _ref.onChange,
      _ref$value = _ref.value,
      value = _ref$value === void 0 ? [] : _ref$value,
      props = _objectWithoutPropertiesLoose(_ref, ["children", "error", "helpText", "id", "label", "onChange", "value"]);

  if (process.env.NODE_ENV !== 'production') {
    // useEffect with an empty array to only warn once per component instance
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(function () {
      // Warn if the component isn't accessible.
      warning(label || props['aria-label'] || props['aria-labelledby'], "<CheckboxGroup> requires a 'label', 'aria-label' or an 'aria-labelledby' to be accessible to screen readers."); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
  }

  function handleChange(event) {
    var newValue = event.target.checked ? value.concat(event.target.value) : value.filter(function (v) {
      return v !== event.target.value;
    });
    onChange && onChange(event, newValue);
  }

  var _useField = useField({
    id: id,
    hasError: error,
    hasHelpText: Boolean(helpText)
  }),
      controlProps = _useField.controlProps,
      helpTextProps = _useField.helpTextProps; // usefield comes with aria-describedby for the help message, we don't set this on the field, but on each radio item


  var ariaDescribedBy = controlProps['aria-describedby'],
      controlPropsWithoutAriaDescribedBy = _objectWithoutPropertiesLoose(controlProps, ["aria-describedby"]);

  var labelId = label ? controlProps.id + "__label" : undefined;
  return /*#__PURE__*/React.createElement(Field, _extends({}, controlPropsWithoutAriaDescribedBy, {
    "aria-labelledby": labelId
  }, props, {
    // Only render the id if it was a provided one, as the generated one from useField doesn't have any
    // purpose for the group element itself
    id: id,
    isInvalid: error,
    role: "group"
  }), label &&
  /*#__PURE__*/
  // @ts-ignore we cant pass htmlFor here, because a div isn't a labelable element
  React.createElement(Label, {
    id: labelId
  }, label), React.Children.map(children, function (child) {
    return /*#__PURE__*/React.cloneElement(child, {
      'aria-describedby': ariaDescribedBy,
      checked: child.props.value !== undefined && value.includes(child.props.value),
      onChange: handleChange
    });
  }), helpText && /*#__PURE__*/React.createElement(HelpText, helpTextProps, helpText));
};
CheckboxGroup.displayName = "CheckboxGroup";

export { Checkbox, CheckboxGroup };
//# sourceMappingURL=index.es.js.map
